import { Product } from '@spa/components/autosuggest/autosuggest.types';
import { SuggestedCategory } from '@spa/lib/graphql/search/searchSuggestions';

export const MAX_AUTOSUGGEST_TERMS = 12;

enum SuggestionPriority {
  SEARCH_TERM = 1,
  CATEGORY = 2,
  SUB_BRAND = 3,
  BRAND = 4
}

interface InternalSuggestionWithPriority {
  readonly normalizedKey: string;
  readonly priority: SuggestionPriority;
  readonly originalData: SuggestedCategory | string;
  readonly textLength: number;
}

export interface ProcessedAutosuggestResults {
  readonly brands: Array<SuggestedCategory>;
  readonly categories: Array<SuggestedCategory>;
  readonly searchTerms: Array<string>;
  readonly products: Array<Product>;
}

export type ProcessedVertexAutosuggestResults = Pick<ProcessedAutosuggestResults, 'searchTerms'>;

type SuggestionsByType = Pick<ProcessedAutosuggestResults, 'brands' | 'categories' | 'searchTerms'>;

export function normalizeText(text: string): string {
  return text
    .normalize('NFD') // Unicode normalization: decompose combined characters
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
    .replace(/['"´`.,-]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

function getSuggestedCategoryPriority(category: SuggestedCategory): SuggestionPriority {
  switch (category.type) {
    case 'BRAND':
      return SuggestionPriority.BRAND;
    case 'SUB_BRAND':
      return SuggestionPriority.SUB_BRAND;
    case 'CATEGORY':
      return SuggestionPriority.CATEGORY;
    default:
      return SuggestionPriority.SEARCH_TERM;
  }
}

function createSuggestionWithPriority(
  suggestion: SuggestedCategory | string,
  priority?: SuggestionPriority
): InternalSuggestionWithPriority {
  let actualPriority: SuggestionPriority;
  let name: string;

  if (typeof suggestion === 'string') {
    actualPriority = SuggestionPriority.SEARCH_TERM;
    name = suggestion;
  } else {
    actualPriority = priority ?? getSuggestedCategoryPriority(suggestion);
    name = suggestion.name;
  }

  return {
    normalizedKey: normalizeText(name),
    priority: actualPriority,
    originalData: suggestion,
    textLength: name.length
  };
}

function removeDuplicatesByPriority(
  suggestions: ReadonlyArray<InternalSuggestionWithPriority>
): ReadonlyArray<InternalSuggestionWithPriority> {
  const suggestionMap = new Map<string, InternalSuggestionWithPriority>();

  for (const suggestion of suggestions) {
    const existing: InternalSuggestionWithPriority | undefined = suggestionMap.get(
      suggestion.normalizedKey
    );

    if (!existing || suggestion.priority > existing.priority) {
      suggestionMap.set(suggestion.normalizedKey, suggestion);
    }
  }

  return Array.from(suggestionMap.values());
}

function separateSuggestionsByType(
  processedSuggestions: ReadonlyArray<InternalSuggestionWithPriority>
): SuggestionsByType {
  const brands: Array<SuggestedCategory> = [];
  const categories: Array<SuggestedCategory> = [];
  const searchTerms: Array<string> = [];

  for (const suggestion of processedSuggestions) {
    if (typeof suggestion.originalData === 'string') {
      searchTerms.push(suggestion.originalData);
    } else {
      const category = suggestion.originalData;
      if (category.type === 'BRAND' || category.type === 'SUB_BRAND') {
        brands.push(category);
      } else {
        categories.push(category);
      }
    }
  }

  return {
    brands,
    categories,
    searchTerms
  };
}

export function processAutosuggestResults(
  popularQueries: ReadonlyArray<string>,
  suggestedBrands: ReadonlyArray<SuggestedCategory>,
  suggestedCategories: ReadonlyArray<SuggestedCategory>,
  products: ReadonlyArray<Product>
): ProcessedAutosuggestResults {
  const allSuggestions: Array<InternalSuggestionWithPriority> = [
    ...popularQueries.map((term) => createSuggestionWithPriority(term)),
    ...suggestedBrands.map((brand) => createSuggestionWithPriority(brand)),
    ...suggestedCategories.map((category) => createSuggestionWithPriority(category))
  ];

  const deduplicatedSuggestions: ReadonlyArray<InternalSuggestionWithPriority> =
    removeDuplicatesByPriority(allSuggestions);
  const { brands, categories, searchTerms }: SuggestionsByType =
    separateSuggestionsByType(deduplicatedSuggestions);

  return {
    brands,
    categories,
    searchTerms,
    products: [...products]
  };
}

export function processVertexAutosuggestResults(
  popularQueries: ReadonlyArray<string>
): ProcessedVertexAutosuggestResults {
  return {
    searchTerms: popularQueries
      .map((term) => term.trim())
      .filter((term) => term.length > 0)
      .slice(0, MAX_AUTOSUGGEST_TERMS)
  };
}
